import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import {
  HeroActionRail,
  railScheduleDef,
  railResidentDef,
  railApplyDef,
  railTourDef,
  railSelfGuidedDef,
} from './HeroActionRail';

const SLIDE_INTERVAL_MS = 7000;

/** Same Ken Burns treatment as the company Vantage hero. */
const kenBurnsStyle: CSSProperties = {
  animation: 'heroKenBurns 30s ease-in-out infinite alternate',
  willChange: 'transform',
  transformOrigin: 'bottom right',
  imageRendering: 'high-quality' as CSSProperties['imageRendering'],
};

const slide1Def: SlotGroupDefinition = {
  label: 'Slide 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // From windriverlodgeapts.com (RentCafe CDN)
      defaultValue:
        'https://cdn.rentcafe.com/dmslivecafe/2/5458/Wind-River-Lodge-047_Drone-Photo_Leasing-Office-Exterior-1_2026-07-14_MLS-Size.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Wind River Lodge leasing office exterior',
    },
  },
};

const slide2Def: SlotGroupDefinition = {
  label: 'Slide 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cdn.rentcafe.com/dmslivecafe/2/5458/Wind-River-Lodge-047_Exterior-Photo_Residential-Buildings-1_2026-07-14_MLS-Size.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Wind River Lodge residential buildings',
    },
  },
};

const slide3Def: SlotGroupDefinition = {
  label: 'Slide 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cdn.rentcafe.com/dmslivecafe/2/5458/Wind-River-Lodge-047_Drone-Photo_Neighborhood-1_2026-07-14_MLS-Size.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Wind River Lodge neighborhood aerial view',
    },
  },
};

const slide4Def: SlotGroupDefinition = {
  label: 'Slide 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cdn.rentcafe.com/dmslivecafe/2/5458/Wind-River-Lodge-047_Exterior-Photo_Residential-Buildings-2_2026-07-14_MLS-Size.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Wind River Lodge apartment buildings',
    },
  },
};

const slide5Def: SlotGroupDefinition = {
  label: 'Slide 5',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cdn.rentcafe.com/dmslivecafe/2/5458/Wind-River-Lodge-047_Drone-Photo_Leasing-Office-Exterior-2_2026-07-14_MLS-Size.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Wind River Lodge community exterior',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function slideClass(isActive: boolean): string {
  return `hero-slide${isActive ? ' hero-slide--active' : ''}`;
}

export function Hero() {
  const slide1 = useSlotGroup('hero.slide_1', slide1Def);
  const slide2 = useSlotGroup('hero.slide_2', slide2Def);
  const slide3 = useSlotGroup('hero.slide_3', slide3Def);
  const slide4 = useSlotGroup('hero.slide_4', slide4Def);
  const slide5 = useSlotGroup('hero.slide_5', slide5Def);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  useCtaSheenStyles();

  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  const filledKey = [slide1.image, slide2.image, slide3.image, slide4.image, slide5.image]
    .map((image, index) => (image ? String(index) : ''))
    .filter(Boolean)
    .join(',');
  const filledIndexes = useMemo(
    () => (filledKey ? filledKey.split(',').map(Number) : []),
    [filledKey],
  );

  const [current, setCurrent] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    if (filledIndexes.length === 0) {
      setCurrent(0);
      return;
    }
    if (!filledIndexes.includes(current)) {
      setCurrent(filledIndexes[0]);
    }
  }, [filledIndexes, current]);

  const advance = useCallback(() => {
    setCurrent((prev) => {
      if (filledIndexes.length <= 1) return prev;
      const pos = filledIndexes.indexOf(prev);
      const safePos = pos < 0 ? 0 : pos;
      return filledIndexes[(safePos + 1) % filledIndexes.length];
    });
  }, [filledIndexes]);

  useEffect(() => {
    if (prefersReducedMotion || filledIndexes.length <= 1) return;
    const id = window.setInterval(advance, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [prefersReducedMotion, filledIndexes.length, advance]);

  // Scroll parallax — same 30% shift as the company Vantage hero.
  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  const imageStyle = prefersReducedMotion ? undefined : kenBurnsStyle;

  return (
    <section
      ref={sectionRef}
      className="hero-section hero-rail-anchor relative w-full overflow-hidden"
      aria-roledescription="carousel"
      aria-label="Property photos"
    >
      {/* Parallax + Ken Burns media plane — matches company Vantage hero. */}
      <div
        ref={imgWrapRef}
        className="hero-bg-media absolute h-[140%] bg-[var(--vibe-color-text)]"
        style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
      >
        {slide1.image && (
          <div className={slideClass(current === 0)} aria-hidden={current !== 0}>
            <EditableSlotGroup groupPath="hero.slide_1" definition={slide1Def} className="block h-full w-full">
              <VibesitesImage
                url={slide1.image}
                alt={slide1.alt || ''}
                priority
                sizes="100vw"
                className="h-full w-full object-cover"
                style={imageStyle}
              />
            </EditableSlotGroup>
          </div>
        )}

        {slide2.image && (
          <div className={slideClass(current === 1)} aria-hidden={current !== 1}>
            <EditableSlotGroup groupPath="hero.slide_2" definition={slide2Def} className="block h-full w-full">
              <VibesitesImage
                url={slide2.image}
                alt={slide2.alt || ''}
                priority={current === 1}
                sizes="100vw"
                className="h-full w-full object-cover"
                style={imageStyle}
              />
            </EditableSlotGroup>
          </div>
        )}

        {slide3.image && (
          <div className={slideClass(current === 2)} aria-hidden={current !== 2}>
            <EditableSlotGroup groupPath="hero.slide_3" definition={slide3Def} className="block h-full w-full">
              <VibesitesImage
                url={slide3.image}
                alt={slide3.alt || ''}
                priority={current === 2}
                sizes="100vw"
                className="h-full w-full object-cover"
                style={imageStyle}
              />
            </EditableSlotGroup>
          </div>
        )}

        {slide4.image && (
          <div className={slideClass(current === 3)} aria-hidden={current !== 3}>
            <EditableSlotGroup groupPath="hero.slide_4" definition={slide4Def} className="block h-full w-full">
              <VibesitesImage
                url={slide4.image}
                alt={slide4.alt || ''}
                priority={current === 3}
                sizes="100vw"
                className="h-full w-full object-cover"
                style={imageStyle}
              />
            </EditableSlotGroup>
          </div>
        )}

        {slide5.image && (
          <div className={slideClass(current === 4)} aria-hidden={current !== 4}>
            <EditableSlotGroup groupPath="hero.slide_5" definition={slide5Def} className="block h-full w-full">
              <VibesitesImage
                url={slide5.image}
                alt={slide5.alt || ''}
                priority={current === 4}
                sizes="100vw"
                className="h-full w-full object-cover"
                style={imageStyle}
              />
            </EditableSlotGroup>
          </div>
        )}
      </div>

      {/* Gradient overlay for text contrast — above media, below copy */}
      <div className="hero-overlay absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content — left-aligned stack; extra right padding on lg+ reserves
          room for the action rail pinned to the right edge. */}
      <div className="hero-content relative z-10 flex h-full flex-col items-start justify-center px-6 sm:px-8 text-left text-white">
        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue="<p>In The Edge District</p>"
          as="h2"
          className="hero-entrance hero-entrance-1 hero-subtitle"
        />

        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue="<p>Property Name</p>"
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta vantage-cta-sheen group/cta inline-flex items-center justify-center"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <HeroActionRail />

      {/* Schema-lint anchors — the interactive rail wrappers live in
          <HeroActionRail>; these hidden anchors keep the same-file wrapper
          check satisfied for the rail_* slot groups declared below. */}
      <EditableSlotGroup groupPath="hero.rail_schedule" definition={railScheduleDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_resident" definition={railResidentDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_apply" definition={railApplyDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_tour" definition={railTourDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_self_guided" definition={railSelfGuidedDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Full viewport height — svh/dvh for mobile browser chrome. */
        .hero-section {
          /* Contain the hero-content (z-10) and action rail (z-20) in their own
             stacking context so the floating rail buttons don't out-stack
             fixed-position third-party widgets (e.g. chatbots) at the page root. */
          isolation: isolate;
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          height: 100vh;
          height: 100svh;
          height: 100dvh;
          min-height: 100vh;
          min-height: 100svh;
          min-height: 100dvh;
        }
        @supports (height: 100dvh) {
          .hero-section {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }

        .hero-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 1.2s ease;
          z-index: 0;
        }
        .hero-slide--active {
          opacity: 1;
          z-index: 1;
        }

        /* Sit above the sliding media plane, below hero copy / rail. */
        .hero-overlay {
          z-index: 2;
          pointer-events: none;
        }

        /* Hero copy — matches the reference Vantage home hero (Raleway,
           95px title / 20px subtitle / 16px CTA on desktop). */
        .hero-content {
          padding-left: 1.5rem;
        }
        @media (min-width: 768px) {
          .hero-content {
            padding-left: 4.4rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-content {
            padding-right: var(--hero-rail-width);
          }
        }
        .hero-title {
          font-family: "Raleway", sans-serif;
          font-size: 42px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          max-width: 14ch;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-title { font-size: 68px; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 95px; }
        }
        .hero-subtitle {
          font-family: "Raleway", sans-serif;
          font-size: 20px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0 0 0.35em;
          max-width: min(48ch, 100%);
        }
        .hero-subtitle p,
        .hero-subtitle span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          letter-spacing: inherit;
          margin: 0;
        }
        .hero-cta {
          font-family: "Raleway", sans-serif;
          font-size: 16px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0;
          text-transform: uppercase;
          min-height: 51px;
          min-width: 25px;
          max-width: 258px;
          width: 100%;
          padding: 0 1.5rem;
          margin-top: 30px;
        }
        @media (min-width: 480px) {
          .hero-cta { width: auto; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-slide { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Property Name</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>In The Edge District</p>',
    },
  },
  slotGroups: {
    'hero.slide_1': slide1Def,
    'hero.slide_2': slide2Def,
    'hero.slide_3': slide3Def,
    'hero.slide_4': slide4Def,
    'hero.slide_5': slide5Def,
    'hero.cta_link': ctaLinkDef,
    'hero.rail_schedule': {
      label: 'Rail · Schedule a Visit',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_resident': {
      label: 'Rail · Resident Access',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_apply': {
      label: 'Rail · Apply Now',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_tour': {
      label: 'Rail · Virtual Tour',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_self_guided': {
      label: 'Rail · Self-Guided Tours',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'custom_self_guided_tour_url',
          suggestedFields: ['custom_self_guided_tour_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
